import { useCallback, useMemo, useState } from 'react';

import { useNotifications } from '@decisiv/ui-components';

import { REQUIRED_FIELDS, toFormValues, toParams } from '../fields';
import updateUser from '../services/editUserService';
import { abandonsState } from '../states';

const NOTIFICATION_DURATION = 8000;

export const SAVED_MESSAGE = 'User settings updated.';
export const FAILED_MESSAGE = 'User settings not updated.';

/**
 * The failure toast, carrying any message the form cannot attach to an input.
 *
 * UserUpdate puts those under `base`: a save can fail on something this page does not
 * edit, and without this the admin would get a bare "not updated" and no reason. The
 * messages are whole sentences, so they read on from the title.
 */
const failureTitle = (errors = {}) =>
  [FAILED_MESSAGE, ...(errors.base || [])].join(' ');

/**
 * Form state for the Edit User page (PD-4068).
 *
 * `saved` is the record as the server last confirmed it, and it is what the header
 * renders: saving can change the name in the title and the accounts or roles under
 * it, so the header follows the response rather than the draft. `values` is the
 * draft the inputs are bound to. Comparing the two is what decides whether there is
 * anything to save.
 */
function useEditUserForm(user) {
  const [saved, setSaved] = useState(user);
  const [values, setValues] = useState(() => toFormValues(user));
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const { dismissAll, notify } = useNotifications();

  const baseline = useMemo(() => toFormValues(saved), [saved]);

  const states = user.options?.states || {};

  const setField = useCallback(
    (name, value) => {
      setValues((current) => {
        const next = { ...current, [name]: value };

        // A state belongs to a country, so choosing a country that does not have the
        // one on file abandons it. Without this the State field would keep the old
        // value — and keep displaying it, because a KeyDS Select only repaints its
        // label when its `value` prop changes, not when its options do — and the save
        // would persist a pair like Canada + Texas, which geocodes to nowhere.
        //
        // Conditional, not unconditional: setting Country on a record that has a
        // state and no country is completing it, and blanking the state there would
        // destroy exactly what stateOptions is there to keep visible.
        if (
          name === 'countryCode' &&
          abandonsState(states, value, current.state)
        ) {
          next.state = '';
        }

        return next;
      });

      // A field the server complained about stops complaining as soon as it is
      // touched. Leaving the message under a field the user has since changed reads
      // as a live error when it no longer is.
      setErrors((current) =>
        current[name] ? { ...current, [name]: undefined } : current,
      );
    },
    [states],
  );

  const isDirty = useMemo(
    () => Object.keys(baseline).some((name) => values[name] !== baseline[name]),
    [baseline, values],
  );

  const hasEveryRequiredField = REQUIRED_FIELDS.every((name) =>
    values[name].trim(),
  );

  // Only the latest save result is worth showing. The provider offers no
  // dismiss-by-id — `notify` always prepends a card, and the `id` option is passed
  // straight through as the card's DOM id rather than used to dedupe, so reusing one
  // would put the same id on two elements — hence dismissing first.
  const announce = useCallback(
    (intent, title) => {
      dismissAll();
      notify({
        intent,
        title,
        duration: NOTIFICATION_DURATION,
        onClose: () => {},
      });
    },
    [dismissAll, notify],
  );

  const save = useCallback(async () => {
    setIsSaving(true);
    const submitted = values;

    try {
      const result = await updateUser(saved.id, toParams(submitted));

      if (result.errors) {
        setErrors(result.errors);
        announce('danger', failureTitle(result.errors));

        return;
      }

      setSaved(result.user);
      // The response is the record as the server now holds it, but the inputs stay
      // editable while a save is in flight, so anything typed since it was submitted
      // is kept. Overwriting wholesale would silently discard keystrokes the admin
      // has already watched appear on screen.
      setValues((current) => {
        const confirmed = toFormValues(result.user);

        return Object.keys(confirmed).reduce(
          (next, name) =>
            current[name] === submitted[name]
              ? next
              : { ...next, [name]: current[name] },
          confirmed,
        );
      });
      setErrors({});
      announce('success', SAVED_MESSAGE);
    } catch (error) {
      // A failure with nothing to say: a dropped connection, a proxy error, a 500.
      announce('danger', FAILED_MESSAGE);
    } finally {
      setIsSaving(false);
    }
  }, [announce, saved.id, values]);

  return {
    canSave: isDirty && hasEveryRequiredField && !isSaving,
    errors,
    isSaving,
    save,
    saved,
    setField,
    values,
  };
}

export default useEditUserForm;
